import React, { useState, useEffect, useContext } from 'react'
import { createPortal } from 'react-dom'
import styles from './ResumeAutoGenerateModal.module.css'
import { StepContext } from '../../StepContainer'
import { useAbTestGtm } from '../../../../../hooks/useAbTestGtm'
import {
    CUSTOMER_SPEND,
    STORE_SIZE,
    LOADING_DELAY_MS,
} from './ResumeAutoGenerateModal.constants'
import { getJobDutiesByOccupation } from './ResumeAutoGenerateModal.helpers'
import { generateResumeText } from './ResumeAutoGenerateModal.generateText'

interface ResumeAutoGenerateModalProps {
    isOpen: boolean
    onClose: () => void
    onGenerate: (_generatedText: string) => void
}

export const ResumeAutoGenerateModal: React.FC<
    ResumeAutoGenerateModalProps
> = ({ isOpen, onClose, onGenerate }) => {
    const { formState } = useContext(StepContext)
    const { dataClickLabel, dataViewLabel } = useAbTestGtm()
    const [selectedDuties, setSelectedDuties] = useState<string[]>([])
    const [selectedSpend, setSelectedSpend] = useState<string>('')
    const [selectedSize, setSelectedSize] = useState<string>('')
    const [mounted, setMounted] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        setMounted(true)
        return () => setMounted(false)
    }, [])

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = ''
        }
        return () => {
            document.body.style.overflow = ''
        }
    }, [isOpen])

    const occupationId = formState.userResumesAttributes[0]?.occupationIds
    const jobDuties = getJobDutiesByOccupation(occupationId)

    useEffect(() => {
        if (isOpen && jobDuties.length > 0) {
            setSelectedDuties(prev =>
                prev.filter(duty => jobDuties.includes(duty))
            )
        }
    }, [occupationId, isOpen, jobDuties])

    if (!isOpen || !mounted) return null

    const handleDutyToggle = (duty: string) => {
        setSelectedDuties(prev =>
            prev.includes(duty)
                ? prev.filter(d => d !== duty)
                : [...prev, duty]
        )
    }

    const handleGenerate = async () => {
        if (selectedDuties.length === 0 || !selectedSpend || !selectedSize) {
            return
        }

        setIsLoading(true)

        try {
            await new Promise(resolve => setTimeout(resolve, LOADING_DELAY_MS))

            const generatedText = generateResumeText({
                formState,
                selectedDuties,
                selectedSpend,
                selectedSize,
            })

            onGenerate(generatedText)
            onClose()
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to generate resume text:', error)
            onClose()
        } finally {
            setIsLoading(false)
        }
    }

    const modalContent = (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
                <div className={styles.header}>
                    <h2
                        className={styles.title}
                        data-view-label={dataViewLabel('EF_StepForm_自動生成')}
                    >
                        仕事内容の自動生成
                    </h2>
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        data-click-label={dataClickLabel(
                            'EF_StepForm_自動生成-戻る'
                        )}
                    >
                        ×
                    </button>
                </div>

                <div className={styles.content}>
                    <div className={styles.section}>
                        <div className={styles.sectionTitleWrapper}>
                            <img
                                src='/foodslaboEf/img/step14/job-duties.png'
                                alt='業務内容'
                                className={styles.sectionIcon}
                            />
                            <h3 className={styles.sectionTitle}>
                                あなたの業務内容を選択(1/3)
                            </h3>
                        </div>
                        <div className={styles.buttonGrid}>
                            {jobDuties.map(duty => (
                                <button
                                    key={duty}
                                    className={`${styles.optionButton} ${
                                        selectedDuties.includes(duty)
                                            ? styles.selected
                                            : ''
                                    }`}
                                    onClick={() => handleDutyToggle(duty)}
                                >
                                    {duty}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={styles.section}>
                        <div className={styles.sectionTitleWrapper}>
                            <img
                                src='/foodslaboEf/img/step14/customer-spend.png'
                                alt='客単価'
                                className={styles.sectionIcon}
                            />
                            <h3 className={styles.sectionTitle}>
                                お店の客単価を選択(2/3)
                            </h3>
                        </div>
                        <div className={styles.buttonGrid}>
                            {CUSTOMER_SPEND.map(spend => (
                                <button
                                    key={spend}
                                    className={`${styles.optionButton} ${
                                        selectedSpend === spend
                                            ? styles.selected
                                            : ''
                                    }`}
                                    onClick={() => setSelectedSpend(spend)}
                                >
                                    {spend}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div
                        className={`${styles.section} ${styles.lastSection}`}
                    >
                        <div className={styles.sectionTitleWrapper}>
                            <img
                                src='/foodslaboEf/img/step14/store-size.png'
                                alt='店舗規模'
                                className={styles.sectionIcon}
                            />
                            <h3 className={styles.sectionTitle}>
                                お店の規模・席数を選択(3/3)
                            </h3>
                        </div>
                        <div className={styles.buttonGrid}>
                            {STORE_SIZE.map(size => (
                                <button
                                    key={size}
                                    className={`${styles.optionButton} ${
                                        selectedSize === size
                                            ? styles.selected
                                            : ''
                                    }`}
                                    onClick={() => setSelectedSize(size)}
                                >
                                    {size}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={styles.buttonSection}>
                        {isLoading ? (
                            <div className={styles.loading}>
                                <span></span>
                                <span></span>
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        ) : (
                            <>
                                <button
                                    className={styles.generateButton}
                                    onClick={handleGenerate}
                                    disabled={
                                        selectedDuties.length === 0 ||
                                        !selectedSpend ||
                                        !selectedSize
                                    }
                                    data-click-label={dataClickLabel(
                                        'EF_StepForm_自動生成-生成する'
                                    )}
                                >
                                    この内容で職務経歴を生成する
                                    <img
                                        src='/foodslaboEf/img/common/nextIcon.svg'
                                        alt='次へ'
                                        className={styles.nextIcon}
                                    />
                                </button>
                                <p className={styles.disclaimer}>
                                    ※既に入力した職務経歴(仕事内容) は上書きされます
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )

    return createPortal(modalContent, document.body)
}
